import React, {useEffect, useMemo, useState} from "react";
import {Button, Form, Spinner, Table} from "react-bootstrap";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";

const today = () => new Date().toISOString().slice(0, 10);

const money = value => value === null || value === undefined
  ? "—"
  : `${Number(value).toLocaleString("pl-PL", {minimumFractionDigits: 2, maximumFractionDigits: 2})} zł`;

const sourceName = value => ({
  MANUAL: "Ręczna stawka zmiany",
  EMPLOYEE_POSITION_PERIOD: "Stawka okresowa",
  EMPLOYEE_POSITION: "Stawka pracownika na stanowisku",
  CONTRACT: "Umowa godzinowa",
  CONTRACT_MONTHLY: "Umowa miesięczna",
  POSITION: "Stawka stanowiska"
}[value] || value || "—");

const profileName = value => ({
  STUDENT_PUPIL: "Uczeń / student < 26",
  KRUS: "KRUS",
  STANDARD_ZUS: "ZUS",
  NO_ZUS: "Bez składek",
  UNKNOWN: "Nieustalony"
}[value] || value || "—");

const errorText = error => error?.response?.data?.message
  || (typeof error?.response?.data === "string" ? error.response.data : null)
  || "Nie udało się wyliczyć kosztu.";

const EmployeeLaborCostOverview = ({employeeId, employeePositions}) => {
  const axiosPrivate = useAxiosPrivate();
  const [date, setDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const positions = useMemo(() => (employeePositions || [])
    .filter(item => item?.id > 0 && item?.position?.id && item?.active !== false)
    .map(item => ({
      assignmentId: item.id,
      positionId: item.position.id,
      positionName: item.position.name || `Stanowisko #${item.position.id}`,
      departmentName: item.position.department?.name || item.department?.name || ""
    })), [employeePositions]);

  const calculate = async () => {
    if (!employeeId || !date || positions.length === 0) {
      setRows([]);
      return;
    }
    setLoading(true);
    const results = await Promise.all(positions.map(async position => {
      try {
        const response = await axiosPrivate.get(`/labor-costs/employee/${employeeId}/position/${position.positionId}`, {params: {date}});
        return {...position, cost: response.data || null, error: null};
      } catch (error) {
        return {...position, cost: null, error: errorText(error)};
      }
    }));
    setRows(results);
    setLoading(false);
  };

  useEffect(() => {
    calculate();
  }, [employeeId]);

  const completeCount = rows.filter(row => row.cost?.complete).length;
  const incompleteCount = rows.filter(row => row.cost && !row.cost.complete).length + rows.filter(row => row.error).length;

  if (!employeeId) {
    return <div className="employee-cost-overview-empty">Zapisz pracownika, aby uruchomić kalkulację kosztów.</div>;
  }

  return (
    <div className="employee-cost-overview">
      <div className="employee-cost-overview-toolbar">
        <div>
          <strong>Podsumowanie stawek i kosztów</strong>
          <small>Efektywna stawka i pełny koszt pracodawcy dla każdego zapisanego, aktywnego stanowiska.</small>
        </div>
        <div className="employee-cost-overview-actions">
          <Form.Control type="date" value={date} onChange={event => setDate(event.target.value)} />
          <Button size="sm" variant="outline-primary" onClick={calculate} disabled={loading || positions.length === 0}>
            {loading ? <Spinner animation="border" size="sm" /> : "Przelicz"}
          </Button>
        </div>
      </div>

      {positions.length === 0 ? (
        <div className="employee-cost-overview-empty">Brak zapisanych aktywnych stanowisk do kalkulacji.</div>
      ) : (
        <div className="table-responsive">
          <Table size="sm" bordered hover className="align-middle employee-cost-overview-table">
            <thead>
              <tr>
                <th>Stanowisko</th>
                <th>Źródło</th>
                <th>Brutto/h</th>
                <th>Netto/h</th>
                <th>Profil</th>
                <th>Składki/h</th>
                <th>Prowizja/h</th>
                <th>Koszt całkowity/h</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => row.error ? (
                <tr key={row.assignmentId}>
                  <td><strong>{row.positionName}</strong>{row.departmentName && <small className="d-block text-muted">{row.departmentName}</small>}</td>
                  <td colSpan={7} className="text-danger">{row.error}</td>
                  <td><span className="badge bg-danger">Błąd</span></td>
                </tr>
              ) : (
                <tr key={row.assignmentId}>
                  <td><strong>{row.positionName}</strong>{row.departmentName && <small className="d-block text-muted">{row.departmentName}</small>}</td>
                  <td>{sourceName(row.cost?.wageRateSource)}</td>
                  <td>{money(row.cost?.wageRate)}</td>
                  <td>{money(row.cost?.netWageRate)}</td>
                  <td>{profileName(row.cost?.insuranceProfile)}</td>
                  <td>{money(row.cost?.employerContributionsHourly)}</td>
                  <td>{money(row.cost?.agencyCommissionHourly)}</td>
                  <td><strong>{money(row.cost?.totalHourlyCost)}</strong></td>
                  <td>
                    <span className={`badge ${row.cost?.complete ? "bg-success" : "bg-warning text-dark"}`}>
                      {row.cost?.complete ? "Kompletny" : "Niepełny"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <div className="employee-cost-overview-footer">
        <span>Data kalkulacji: <strong>{date}</strong></span>
        {rows.length > 0 && <span>Kompletne: <strong>{completeCount}</strong> · wymagają uwagi: <strong>{incompleteCount}</strong></span>}
        <span>Podgląd korzysta z danych zapisanych w bazie. Po zmianie stawki zapisz kartotekę i przelicz ponownie.</span>
      </div>
    </div>
  );
};

export default EmployeeLaborCostOverview;
